import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import type { platform } from '@platform/contracts';
import {
  Alert,
  AuthShell,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  ListSkeleton,
  Select,
  Stack,
  Typography,
} from '@platform/design-system';
import {
  LEGAL_DOCUMENT_LABEL,
  NOTICE_CHANNEL_LABEL,
  describeError,
  toAppError,
  useSession,
} from '@platform/app-kit';
import { usePlans, useSignupDocuments, useStartPlan } from '../api/queries.ts';
import { AUTH_FRAME } from '../shell/brand.ts';
import { useDocumentTitle } from '../shell/document-title.ts';

const TICK_ALL = 'Tick each box above to continue.';
const DOCUMENTS_CHANGED =
  'These documents were just updated. Read them again, then tick each box to continue.';

/** The documents as shown, by document and version, to tell whether a refetch changed them. */
const shownKey = (docs: readonly platform.LegalDocument[]) =>
  docs.map((d) => `${d.document}@${d.version}`).join(',');

/**
 * `/subscribe` — signed in, no plan yet: choose the plan (one flat monthly package today) and
 * continue to the payment page. When the product asks people to accept documents (ADR
 * tenant-export-deletion-and-consent), one unticked box per document and one for notices by
 * email come first; the sign-up carries the versions shown. `POST /signup/personal` reserves the
 * person's own tenant, or resumes the one they already started; `/signup/complete` then waits for
 * the payment page and sends the browser there.
 */
export function SubscribePage({ open }: { open: boolean }) {
  const { principal, signOut } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plans = usePlans();
  const documents = useSignupDocuments();
  const start = useStartPlan();
  const [chosen, setChosen] = useState('');
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const [notices, setNotices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle('Choose your plan');

  if (principal?.tenantId) return <Navigate to="/" replace />;

  const list = plans.data ?? [];
  const only = list.length === 1 ? list[0] : undefined;
  const selected = only?.packageId ?? chosen;
  const cancelled = params.get('cancelled') === '1';
  const docs = documents.data ?? [];
  const asks = docs.length > 0;
  // Never sign up without knowing what the person has to accept.
  const documentsReady = documents.isSuccess;

  const continueToPayment = () => {
    if (!selected) {
      setError('Choose a plan to continue.');
      return;
    }
    if (asks && (!docs.every((d) => agreed[d.document]) || !notices)) {
      setError(TICK_ALL);
      return;
    }
    setError(null);
    const shown = shownKey(docs);
    start.mutate(
      {
        packageId: selected,
        ...(asks
          ? {
              accepted: docs.map((d) => ({ document: d.document, version: d.version })),
              noticeChannel: 'email' as const,
            }
          : {}),
      },
      {
        onSuccess: (s) =>
          void navigate(`/signup/complete?slug=${encodeURIComponent(s.slug)}`, { replace: true }),
        onError: (e) =>
          void (async () => {
            const status = toAppError(e).status;
            if ((status === 409 || status === 400) && asks) {
              const fresh = await documents.refetch();
              if (shownKey(fresh.data ?? []) !== shown) {
                setAgreed({});
                setNotices(false);
                setError(DOCUMENTS_CHANGED);
                return;
              }
              if (status === 400) {
                setError(TICK_ALL);
                return;
              }
            }
            setError(startFailure(e));
          })(),
      },
    );
  };

  return (
    <AuthShell
      brandName={AUTH_FRAME.brandName}
      pitch="One plan. Billed monthly."
      supporting="You see the price and pay on the next page. Your account opens as soon as the payment goes through."
      title="Choose your plan"
      lead={principal ? `Signed in as ${principal.email}.` : undefined}
    >
      <Stack spacing={2}>
        {cancelled && (
          <Alert severity="info" role="status">
            You left the payment page, so nothing was charged. Continue when you’re ready.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!open ? (
          <Alert severity="info" role="status">
            Plans aren’t available here yet. Check back soon.
          </Alert>
        ) : plans.isLoading ? (
          <ListSkeleton rows={1} />
        ) : plans.isError ? (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={() => void plans.refetch()}>
                Try again
              </Button>
            }
          >
            {describeError(plans.error, 'We couldn’t load the plans just now.')}
          </Alert>
        ) : list.length === 0 ? (
          <Alert severity="info" role="status">
            No plan is open for sign-up yet. Check back soon.
          </Alert>
        ) : only ? (
          <Box
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
            aria-label="Your plan"
            role="group"
          >
            <Typography variant="subtitle1" component="p">
              {only.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Billed every month. The price is on the payment page.
            </Typography>
          </Box>
        ) : (
          <Select
            label="Plan"
            value={chosen}
            onChange={(value) => setChosen(String(value))}
            placeholder="Choose a plan"
            options={list.map((p) => ({ value: p.packageId, label: p.name }))}
          />
        )}
        {open && documents.isError && (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={() => void documents.refetch()}>
                Try again
              </Button>
            }
          >
            We couldn’t load what you’re asked to agree to. Try again in a moment.
          </Alert>
        )}
        {open && asks && (
          <FormGroup aria-label="Before you continue">
            {docs.map((d) => (
              <FormControlLabel
                key={d.document}
                control={
                  <Checkbox
                    checked={agreed[d.document] === true}
                    onChange={(event) =>
                      setAgreed((current) => ({ ...current, [d.document]: event.target.checked }))
                    }
                  />
                }
                label={
                  <>
                    {LEGAL_DOCUMENT_LABEL[d.document].before}
                    <Link href={d.url} target="_blank" rel="noopener noreferrer">
                      {LEGAL_DOCUMENT_LABEL[d.document].link}
                    </Link>
                  </>
                }
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  checked={notices}
                  onChange={(event) => setNotices(event.target.checked)}
                />
              }
              label={NOTICE_CHANNEL_LABEL}
            />
          </FormGroup>
        )}
        <Button
          variant="contained"
          size="large"
          onClick={continueToPayment}
          busy={start.isPending}
          busyLabel="Preparing your payment page…"
          disabled={!open || list.length === 0 || !documentsReady}
        >
          Continue to payment
        </Button>
        <Typography variant="body2" color="text.secondary">
          Not you?{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}
          >
            Sign out
          </Link>
        </Typography>
      </Stack>
    </AuthShell>
  );
}

/**
 * Plain words for a refused start. A conflict's own message is written for operators (it names
 * the package and the rule), so it is never shown here.
 */
function startFailure(e: unknown): string {
  const err = toAppError(e);
  if (err.status === 409 || err.status === 404)
    return 'This plan can’t be chosen right now. Try again in a little while.';
  if (err.status === 401) return 'Your session has ended. Sign out, then sign in again.';
  return describeError(e, 'We couldn’t start your plan. Try again.');
}
